// 첨부파일 미리보기용 same-origin 탭 간 데이터 전달.
//
// /viewer 탭은 WASM(SharedArrayBuffer) 구동을 위해 COOP:same-origin +
// COEP:require-corp 로 cross-origin isolation 되어 있어, 격리 안 된 레코드
// 탭에서 만든 blob: URL 을 조회하지 못한다 (agent cluster 분리 →
// ERR_FILE_NOT_FOUND). 그래서 blob: URL 을 쿼리로 넘기는 대신, 이미
// 복호화된 Blob 을 BroadcastChannel 로 직접 전달한다. BroadcastChannel 은
// 격리와 무관하게 same-origin 컨텍스트 간 통신이 가능하다.
//
// 로드 순서 race 를 없애기 위해 handshake 를 쓴다: 수신자(뷰어)가 'request'
// 를 (재시도하며) 보내면 송신자(레코드 탭)가 'data' 로 응답한다.

const CHANNEL_NAME = 'attachment-preview'
const SERVE_MAX_MS = 60000
const RECEIVE_TIMEOUT_MS = 20000
const REQUEST_RETRY_MS = 500

function isSupported() {
  return typeof BroadcastChannel !== 'undefined'
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// 레코드 탭(송신자): Blob 을 서빙하고 transferId 를 반환. BroadcastChannel
// 미지원 시 null 반환 → 호출부가 레거시 blob: URL 경로로 폴백.
export function serveAttachmentForPreview(blob, { name, mimeType } = {}) {
  if (!isSupported() || blob == null) return null

  const id = generateId()
  const channel = new BroadcastChannel(CHANNEL_NAME)
  let closed = false

  const cleanup = () => {
    if (closed) return
    closed = true
    try {
      channel.close()
    } catch {
      // 이미 닫힌 경우 무시
    }
  }

  channel.onmessage = (e) => {
    const msg = e.data
    if (!msg || msg.id !== id) return
    if (msg.type === 'request') {
      channel.postMessage({ type: 'data', id, blob, name, mimeType })
    } else if (msg.type === 'received') {
      cleanup()
    }
  }

  // 수신자가 안 나타나도 채널이 영원히 열려있지 않도록 안전 정리.
  setTimeout(cleanup, SERVE_MAX_MS)
  return id
}

// 뷰어 탭(수신자): transferId 로 Blob 을 요청/수신.
export function receiveAttachmentForPreview(id, { timeoutMs = RECEIVE_TIMEOUT_MS } = {}) {
  return new Promise((resolve, reject) => {
    if (!isSupported()) {
      reject(new Error('BroadcastChannel not supported'))
      return
    }

    const channel = new BroadcastChannel(CHANNEL_NAME)
    let done = false
    let retryTimer = null
    let timeoutTimer = null

    const finish = (fn, arg) => {
      if (done) return
      done = true
      if (retryTimer != null) clearInterval(retryTimer)
      if (timeoutTimer != null) clearTimeout(timeoutTimer)
      try {
        channel.close()
      } catch {
        // 무시
      }
      fn(arg)
    }

    channel.onmessage = (e) => {
      const msg = e.data
      if (!msg || msg.id !== id || msg.type !== 'data') return
      channel.postMessage({ type: 'received', id })
      finish(resolve, { blob: msg.blob, name: msg.name, mimeType: msg.mimeType })
    }

    const request = () => channel.postMessage({ type: 'request', id })
    request()
    retryTimer = setInterval(request, REQUEST_RETRY_MS)
    timeoutTimer = setTimeout(
      () => finish(reject, new Error('attachment preview transfer timeout')),
      timeoutMs,
    )
  })
}
